import { useMemo, useState, type ReactNode } from 'react';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { IntegrationLogo } from '@/modules/integrations/components/integration-logo';
import { AccessIcon } from '@/modules/back-testing/datasets/tools/tool-access';
import { toolKey } from '@/modules/back-testing/datasets/tools/tool-schema';
import { humanize } from '@/modules/simulator/ui/text';
import type { ToolDefinitionAction } from '@/services/toolDefinition.service';
import { isWriteTool } from '../use-step-tools';
import { useCustomToolBuilder } from './builder-context';

/** The brand's tools by integration, searched by label, integration or the tool's own name, opened from `children`. */
export const StepPicker = ({ children }: { children: ReactNode }) => {
	const { stepTools, addStep } = useCustomToolBuilder();
	const [open, setOpen] = useState(false);
	const groups = useMemo(() => {
		const byProvider = new Map<string, ToolDefinitionAction[]>();
		for (const tool of stepTools.tools) byProvider.set(tool.provider, [...(byProvider.get(tool.provider) ?? []), tool]);
		return [...byProvider.entries()].sort(([a], [b]) => a.localeCompare(b));
	}, [stepTools.tools]);

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>{children}</PopoverTrigger>
			<PopoverContent align='start' className='w-80 p-0'>
				<Command>
					<CommandInput placeholder='Search tools' className='text-[13px]' />
					<CommandList>
						<CommandEmpty className='py-6 text-center text-xs text-gray-800'>
							{stepTools.isLoading ? 'Loading tools' : stepTools.isError ? 'The tools could not be loaded.' : 'No tool matches.'}
						</CommandEmpty>
						{groups.map(([provider, tools]) => (
							<CommandGroup key={provider} heading={humanize(provider)}>
								{tools.map((tool) => (
									<CommandItem
										key={toolKey(tool.provider, tool.tool)}
										value={toolKey(tool.provider, tool.tool)}
										keywords={[tool.label, tool.provider]}
										onSelect={() => {
											addStep(tool);
											setOpen(false);
										}}
										className='gap-2'
									>
										<IntegrationLogo provider={tool.provider} className='size-4' decorative />
										<span className='min-w-0 flex-1 truncate text-[13px]'>{tool.label}</span>
										<AccessIcon riskLevel={isWriteTool(tool) ? 'high' : 'low'} />
									</CommandItem>
								))}
							</CommandGroup>
						))}
					</CommandList>
				</Command>
			</PopoverContent>
		</Popover>
	);
};
